import { getScopedDomain } from '../lib/tenantScope';
// LDAP / Active Directory contact directory sync (3CX "company phonebook from
// AD" parity). Per-tenant settings + Test Connection + Sync Now + last-run
// status. Synced contacts land in the shared company address book (Contacts)
// tagged source='ldap'. The bind password is write-only: the server only ever
// reports has_bind_password — leave the field blank to keep the stored one.

import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { FolderSync, PlugZap, RefreshCw, BookUser } from 'lucide-react';
import { api } from '../lib/api';
import { useToast } from '../components/Toast';

export default function LdapSync() {
  const me = api.me();
  const qc = useQueryClient();
  const toast = useToast();
  const dom = getScopedDomain(me?.domain_uuid);

  const { data: settings } = useQuery({
    queryKey: ['ldap-sync', dom],
    queryFn: () => api.ldapSync.get(dom!),
    enabled: !!dom,
  });
  const { data: status } = useQuery({
    queryKey: ['ldap-sync-status', dom],
    queryFn: () => api.ldapSync.status(dom!),
    enabled: !!dom,
    refetchInterval: 30_000,
  });

  const [form, setForm] = useState<any>({
    enabled: false, host: '', port: '', ssl: false, starttls: false,
    bind_dn: '', bind_password: '', base_dn: '',
    search_filter: '', sync_interval_minutes: 360,
  });
  useEffect(() => {
    if (!settings) return;
    setForm((f: any) => ({
      ...f,
      enabled: !!settings.enabled,
      host: settings.host || '',
      port: settings.port || '',
      ssl: !!settings.ssl,
      starttls: !!settings.starttls,
      bind_dn: settings.bind_dn || '',
      bind_password: '', // write-only — never round-trips
      base_dn: settings.base_dn || '',
      search_filter: settings.search_filter || '',
      sync_interval_minutes: settings.sync_interval_minutes || 360,
    }));
  }, [settings]);

  const set = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));
  const payload = () => ({ domain_uuid: dom, ...form, port: form.port ? Number(form.port) : null });

  const save = useMutation({
    mutationFn: () => api.ldapSync.save(payload()),
    onSuccess: () => {
      toast('success', 'LDAP settings saved');
      qc.invalidateQueries({ queryKey: ['ldap-sync', dom] });
      qc.invalidateQueries({ queryKey: ['ldap-sync-status', dom] });
    },
    onError: (e: any) => toast('error', e?.message || 'Could not save LDAP settings'),
  });

  const testConn = useMutation({
    mutationFn: () => api.ldapSync.test(payload()),
    onSuccess: (r: any) => toast('success', `Connected — ${r.matched} entries match, ${r.usable} usable contacts`),
    onError: (e: any) => toast('error', e?.message || 'LDAP connection failed'),
  });

  const syncNow = useMutation({
    mutationFn: () => api.ldapSync.syncNow(dom!),
    onSuccess: (r: any) => {
      toast('success', `Synced ${r.total} contacts — ${r.added} added, ${r.updated} updated, ${r.vanished} removed`);
      qc.invalidateQueries({ queryKey: ['ldap-sync-status', dom] });
    },
    onError: (e: any) => toast('error', e?.message || 'Sync failed'),
  });

  return (
    <div className="space-y-4 max-w-3xl">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold flex items-center gap-2"><FolderSync size={22} /> Directory Sync (LDAP / AD)</h1>
        <Link to="/contacts" className="btn"><BookUser size={14} className="inline mr-1" /> Company Phonebook</Link>
      </div>

      <div className="card p-4 space-y-4">
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={form.enabled} onChange={e => set('enabled', e.target.checked)} />
          <span className="font-medium">Enable scheduled sync</span>
          <span className="text-xs text-textSecondary">pulls the directory into the company phonebook on an interval</span>
        </label>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm mb-1">LDAP server host</label>
            <input className="input w-full font-mono text-sm" value={form.host} onChange={e => set('host', e.target.value)} placeholder="dc1.corp.example.com" />
          </div>
          <div>
            <label className="block text-sm mb-1">Port <span className="text-textSecondary text-xs">(blank = 389 / 636)</span></label>
            <input className="input w-full" value={form.port} onChange={e => set('port', e.target.value.replace(/\D/g, ''))} placeholder="389" />
          </div>
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.ssl} onChange={e => set('ssl', e.target.checked)} /> LDAPS (SSL)
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.starttls} disabled={form.ssl} onChange={e => set('starttls', e.target.checked)} /> StartTLS
            </label>
          </div>
          <div>
            <label className="block text-sm mb-1">Sync interval (minutes)</label>
            <input className="input w-full" value={form.sync_interval_minutes} onChange={e => set('sync_interval_minutes', e.target.value.replace(/\D/g, ''))} placeholder="360" />
          </div>
          <div>
            <label className="block text-sm mb-1">Bind DN (service account)</label>
            <input className="input w-full font-mono text-xs" value={form.bind_dn} onChange={e => set('bind_dn', e.target.value)} placeholder="CN=svc-phonebook,OU=Service,DC=corp,DC=example,DC=com" />
          </div>
          <div>
            <label className="block text-sm mb-1">
              Bind password
              {settings?.has_bind_password && <span className="text-xs text-success ml-2">stored — blank keeps it</span>}
            </label>
            <input className="input w-full" type="password" value={form.bind_password} onChange={e => set('bind_password', e.target.value)}
              placeholder={settings?.has_bind_password ? '••••••••' : ''} autoComplete="new-password" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm mb-1">Base DN</label>
            <input className="input w-full font-mono text-xs" value={form.base_dn} onChange={e => set('base_dn', e.target.value)} placeholder="OU=Staff,DC=corp,DC=example,DC=com" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm mb-1">Search filter <span className="text-textSecondary text-xs">(blank = AD default: persons with a phone number)</span></label>
            <input className="input w-full font-mono text-xs" value={form.search_filter} onChange={e => set('search_filter', e.target.value)} placeholder="(&(objectClass=person)(telephoneNumber=*))" />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button className="btn-primary" onClick={() => save.mutate()} disabled={save.isPending}>Save</button>
          <button className="btn" onClick={() => testConn.mutate()} disabled={testConn.isPending || !form.host || !form.base_dn}>
            <PlugZap size={14} className="inline mr-1" /> {testConn.isPending ? 'Testing…' : 'Test Connection'}
          </button>
          <button className="btn" onClick={() => syncNow.mutate()} disabled={syncNow.isPending || !settings?.configured}>
            <RefreshCw size={14} className={`inline mr-1 ${syncNow.isPending ? 'animate-spin' : ''}`} /> {syncNow.isPending ? 'Syncing…' : 'Sync Now'}
          </button>
        </div>
      </div>

      <div className="card p-4">
        <h2 className="font-medium mb-2">Last sync</h2>
        {!status?.last_sync_at && <div className="text-sm text-textSecondary">Never run.</div>}
        {status?.last_sync_at && (
          <div className="text-sm space-y-1">
            <div>
              {status.last_sync_status === 'ok'
                ? <span className="badge bg-success/20 text-success">ok</span>
                : <span className="badge bg-danger/20 text-danger">error</span>}
              <span className="ml-2 text-textSecondary">{new Date(status.last_sync_at).toLocaleString()}</span>
            </div>
            {status.last_sync_status === 'ok' && (
              <div className="text-textSecondary">
                {status.last_sync_total} matched — {status.last_sync_added} added, {status.last_sync_updated} updated, {status.last_sync_vanished} removed
              </div>
            )}
            {status.last_sync_error && <div className="text-danger font-mono text-xs">{status.last_sync_error}</div>}
          </div>
        )}
        <p className="text-xs text-textSecondary mt-3">
          Removed directory entries are kept in the phonebook marked <span className="font-mono">ldap_vanished</span> (never
          hard-deleted). Purge them from Contacts with bulk delete by source if needed.
        </p>
      </div>
    </div>
  );
}
